import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../../core/auth/auth.service';
import { AuthUtils } from '../../core/auth/auth.utils';
import { Observable, catchError, switchMap, throwError } from 'rxjs';
import { Router } from '@angular/router';

/**
 * Retornar se a requisição é a própria troca de ID Token do Google por um JWT da HMAX.
 * Usada para evitar laço de reautenticação quando essa rota responde 401.
 */
const isGoogleLoginRequest = (req: HttpRequest<unknown>): boolean => req.url.includes('/auth/login-google');

/**
 * R1/D1 do PRD: as rotas públicas do cardápio (`/api/public/*`) NÃO podem receber o
 * token de plataforma. Um gerente logado que abre um QR no mesmo navegador não pode ter
 * o accessToken dele vazando para o endpoint público — e um 401 de sessão de mesa não
 * pode disparar o logout/renovação da sessão da plataforma. O token de sessão de mesa,
 * quando existir (F6), é anexado pelo próprio fluxo do cardápio, nunca por este interceptor.
 */
const isPublicMenuRequest = (req: HttpRequest<unknown>): boolean => req.url.split('?')[0].includes('/public/');

/**
 * Desconectar o usuário e recarregar o app quando não for possível renovar a sessão.
 */
const signOutAndReload = (authService: AuthService, currentUrl: string): void => {
  authService.signOut();
  if (!currentUrl.includes('/sign-in')) {
    location.reload();
  }
};

/**
 * Intercept
 *
 * @param req
 * @param next
 */
export const authInterceptor = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);

  // Clone the request object
  let newReq = req.clone();

  const router = inject(Router);
  const currentUrl = router.url;

  const tokenExpired = AuthUtils.isTokenExpired(authService.accessToken);
  const accessToken = authService.accessToken;

  // Request
  //
  // Se o token ainda é válido, inclui no header Authorization de todas as requisições
  // Isso força o servidor retornar "401 Unauthorized" para as rotas de API protegidas
  // as quais  nosso Interceptor de response irá capturar e remover o token do local storage
  // ao desconectar o usuário do aplicativo
  //   if (authService.accessToken && !AuthUtils.isTokenExpired(authService.accessToken)) {
  if (accessToken && !tokenExpired && !isPublicMenuRequest(req)) {
    newReq = req.clone({
      headers: req.headers.set('Authorization', 'Bearer ' + authService.accessToken),
    });
  }

  // Response
  return next(newReq).pipe(
    catchError((error) => {
      if (!(error instanceof HttpErrorResponse)) {
        return throwError(() => error);
      }

      // Rota pública do cardápio: erro nunca afeta a sessão da plataforma (sem logout/reauth).
      if (isPublicMenuRequest(req)) {
        return throwError(() => error);
      }

      // Bloqueio por faixa de horário: desconectar e informar o motivo na tela de login
      if (error.status === 403 && error.error?.code === 'ACCESS_OUT_OF_SCHEDULE') {
        authService.signOut();
        router.navigate(['/sign-in'], {
          state: { alert: { type: 'error', message: 'access-out-of-schedule' } },
        });
        return throwError(() => error);
      }

      if (error.status !== 401) {
        return throwError(() => error);
      }

      console.error(error);

      // 401 na própria rota de renovação: não tentar reautenticar de novo (evita laço).
      // Deixa o fluxo de renovação (tryGoogleSilentReauth) capturar o erro.
      if (isGoogleLoginRequest(req)) {
        return throwError(() => error);
      }

      // Antes de desconectar, tenta renovar a sessão silenciosamente reaproveitando
      // a conta Google ainda autenticada no Firebase
      return authService.tryGoogleSilentReauth().pipe(
        switchMap((renewed) => {
          if (renewed) {
            // Repete a requisição original já com o novo token
            const retryReq = req.clone({
              headers: req.headers.set('Authorization', 'Bearer ' + authService.accessToken),
            });
            return next(retryReq);
          }

          // Renovação indisponível ou falhou: mantém o comportamento de logout
          signOutAndReload(authService, currentUrl);
          return throwError(() => error);
        }),
      );
    }),
  );
};
